import { Lock as LockIcon, WarningAmberRounded as WarningIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { formatAppointmentTimeDisplay, statusColor } from "./appointmentUtils";

const clashLabel = (conflict) => {
  const dims = [conflict.clientClash && "Client", conflict.officerClash && "Officer"].filter(
    Boolean
  );
  if (!dims.length) return "Clash";
  return `${dims.join(" & ")} clash`;
};

const ConflictRow = ({ conflict }) => {
  const time = formatAppointmentTimeDisplay(conflict) || "No time set";
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          {conflict.redacted ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <LockIcon sx={{ fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Private appointment
              </Typography>
            </Stack>
          ) : (
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {conflict.title || "Appointment"}
            </Typography>
          )}

          {!conflict.redacted && conflict.clientLabel && (
            <Typography variant="body2" color="text.secondary">
              Client: {conflict.clientLabel}
            </Typography>
          )}
          {!conflict.redacted && conflict.withUserName && (
            <Typography variant="body2" color="text.secondary">
              With: {conflict.withUserName}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {time}
          </Typography>
        </Box>

        <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          {conflict.status && (
            <Chip
              size="small"
              label={conflict.status}
              color={statusColor[conflict.status] || "default"}
              variant="outlined"
            />
          )}
          <Chip size="small" label={clashLabel(conflict)} color="warning" variant="outlined" />
        </Stack>
      </Stack>
    </Paper>
  );
};

/**
 * Soft warning shown before saving an appointment that clashes with an existing one on the same
 * client or the same "with" officer. The user may cancel or schedule anyway. Conflicts on
 * appointments the user cannot see are shown redacted (time slot only).
 */
const AppointmentConflictDialog = ({ open, conflicts = [], onCancel, onProceed, isSubmitting }) => {
  const count = conflicts.length;
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningIcon color="warning" />
          <span>Possible scheduling conflict</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {count} existing appointment{count === 1 ? "" : "s"} on this date overlap with the same
          client or officer. Review before continuing.
        </Typography>
        <Stack spacing={1}>
          {conflicts.map((conflict) => (
            <ConflictRow key={conflict.appointmentId} conflict={conflict} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={onProceed} disabled={isSubmitting}>
          Schedule anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentConflictDialog;
